import React, { useState, useEffect, useCallback } from "react";
import cn from "classnames";
import styles from "./StudentDetail.module.sass";
import { useLocation, useParams, Link, useNavigate } from "react-router-dom";

// Screens
import NameAndDescription from "./NameAndDescription";
import DocumentsAndFiles from "./DocumentsAndFiles";
import MemosAndNotes from "./MemosAndNotes";
import ContractAndFiles from "./ContractAndFiles";

// Components
import Card from "../../components/Card";
import Dropdown from "../../components/Dropdown";
import LetterAvatar from "../../components/LetterAvatar";
import Icon from "../../components/Icon";
import Actions from "../../components/Actions";
import ModalDeleteStudent from "../../components/ModalDeleteStudent";

// Services
import { getStudentById, sendStudentCredentials } from "../../services/student.service";
import { showSuccess, showError } from "../../services/toast.service";

// Context
import { useAuth } from "../../contexts/AuthContext";

// data
const navigation = ["Coordonnées", "Documents", "Contrats", "Mémos"];

const StudentDetail = () => {
  const [activeIndex, setActiveIndex] = useState(0);
  const [student, setStudent] = useState(null);
  const [updatingAction, setUpdatingAction] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const { id } = useParams();
  const { isAdmin } = useAuth();

  const formatDate = useCallback((dateString) => {
    if (!dateString) return 'N/A';

    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return dateString;

      return new Intl.DateTimeFormat('fr-FR', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric'
      }).format(date);
    } catch (e) {
      return dateString;
    }
  }, []);

  // Fonction pour ouvrir la modal de suppression (Admin uniquement)
  const handleDeleteStudent = () => {
    if (!isAdmin) {
      showError("Seuls les administrateurs peuvent supprimer un élève");
      return;
    }
    setShowDeleteModal(true);
  };

  // Callback après suppression réussie
  const handleStudentDeleted = () => {
    showSuccess("Élève supprimé avec succès");
    navigate("/admin/students");
  };

  // Fonction pour renvoyer les identifiants
  const handleSendCredentials = async () => {
    if (!student) return;

    try {
      setUpdatingAction(true);

      await sendStudentCredentials(student.id);

      showSuccess('Email de réinitialisation envoyé avec succès à l\'étudiant!');
    } catch (error) {
      console.error('Erreur lors du renvoi des identifiants:', error);
      showError(error.message || 'Erreur lors du renvoi des identifiants');
    } finally {
      setUpdatingAction(false);
    }
  };

  // Fonction pour mettre à jour le statut du dossier
  const handleFileStatusUpdate = useCallback(async () => {
    try {
      const updatedStudent = await getStudentById(id, { expand: 'formation,company' });
      setStudent(updatedStudent);
    } catch (error) {
      console.error('Erreur lors de la mise à jour du statut:', error);
    }
  }, [id]);

  useEffect(() => {
    if (location.state?.student) {
      setStudent(location.state.student);
      return;
    }

    const loadStudent = async () => {
      try {
        const data = await getStudentById(id, { expand: 'formation,company' });

        if (data.education && typeof data.education === 'string') {
          try {
            data.education = JSON.parse(data.education);
          } catch (e) {
            console.error('Erreur lors du parsing de education:', e);
            data.education = {};
          }
        }

        setStudent(data);
      } catch (err) {
        console.error('Erreur:', err);
      }
    };

    loadStudent();
  }, [id, location.state]);

  return (
    <>
      <div className={styles.shop}>
        <Link className={cn("button-white", styles.button)} to="/admin/students">
          <Icon name="arrow-left" />
          Retour
        </Link>

        <div className={styles.background}>
          <img src="/images/content/bg-shop.jpg" alt="Background" />
        </div>

        <Card className={styles.card}>
          {student && (
            <div className={styles.profile}>
              <div className={styles.details}>
                <div className={styles.profilAvatar}>
                  <LetterAvatar
                    firstName={student.first_name}
                    lastName={student.last_name}
                    size={80}
                  />
                </div>
                <div className={styles.wrap}>
                  <div className={cn("h4", styles.man)}>
                    {student.first_name} {student.last_name}
                  </div>
                  <div className={styles.login}>
                    {(student.expand?.formation && Object.keys(student.expand.formation).length > 0) ?
                      student.expand.formation.exact_title_of_qualification : 'Aucune formation'}
                  </div>
                </div>
              </div>

              <div className={styles.statusContainer}>
                <div className={styles.statusGroup}>
                  <div className={styles.login}>Etat du dossier</div>
                  <div className={cn(
                    styles.statusBadge,
                    'status-type',
                    student.file_status === 'Complet' ? 'status-green-dark' : 'status-red-dark'
                  )}>
                    {student.file_status}
                  </div>
                </div>
                <div className={styles.statusGroup}>
                  <div className={styles.login}>Statut pédagogique</div>
                  <div className={cn(
                    styles.statusBadge,
                    'status-type',
                    student.status === 'En cours' ? 'status-green-dark' : 'status-red-dark'
                  )}>
                    {student.status}
                  </div>
                </div>
                <div className={styles.statusGroup}>
                  <div className={styles.login}>Date d'inscription</div>
                  <div className={cn(
                    styles.statusBadge,
                    'status-type',
                    'status-blue-dark'
                  )}>
                    {formatDate(student.inscription_at)}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className={styles.control}>
            <div className={cn(styles.nav, styles.desktopNav)}>
              {navigation.map((x, index) => (
                <button
                  className={cn(styles.link, {
                    [styles.active]: index === activeIndex,
                  })}
                  onClick={() => setActiveIndex(index)}
                  key={index}
                >
                  {x}
                </button>
              ))}
            </div>

            <div className={styles.actionContainer}>
              {updatingAction ? (
                <div className={styles.loading}>Mise à jour en cours...</div>
              ) : (
                <Actions
                  className={styles.actions}
                  classActionsHead={styles.actionsHead}
                  items={[
                    {
                      title: 'Réinitialiser les identifiants',
                      action: handleSendCredentials
                    },
                    ...(isAdmin ? [{
                      title: "Supprimer l'élève",
                      action: handleDeleteStudent
                    }] : [])
                  ]}
                  small
                />
              )}
            </div>

            <div className={styles.mobileNav}>
              <Dropdown
                className={styles.dropdown}
                value={navigation[activeIndex]}
                setValue={(value) => {
                  const index = navigation.findIndex(x => x === value);
                  if (index !== -1) setActiveIndex(index);
                }}
                options={navigation}
              />
            </div>
          </div>
        </Card>

        <div className={styles.wrap}>
          {activeIndex === 0 && student && <NameAndDescription student={student} />}
          {activeIndex === 1 && student && (
            <DocumentsAndFiles 
              studentId={student.id} 
              onFileStatusUpdate={handleFileStatusUpdate}
              currentFileStatus={student.file_status}
            />
          )}
          {activeIndex === 2 && student && <ContractAndFiles studentId={student.id} />}
          {activeIndex === 3 && student && <MemosAndNotes studentId={student.id} />}
        </div>
      </div>

      {/* Modal de confirmation de suppression */}
      <ModalDeleteStudent
        visible={showDeleteModal}
        onClose={() => setShowDeleteModal(false)}
        studentId={student?.id}
        studentName={student ? `${student.first_name} ${student.last_name}` : ''}
        onDeleted={handleStudentDeleted}
      />
    </>
  );
};

export default StudentDetail;